/**
 * The mix sheet. Trims in dB that bring each cue to its rung of the loudness ladder, set from
 * the sound lab's K-weighted readings (LUFS, loudest 400 ms) of the raw cue. Placement gains in
 * the director then set the in-game ladder:
 *
 *   enemy ultimate theme  ≈ −12 LUFS   (the loudest thing you ever hear)
 *   enemy hero ult line   ≈ −12        ally ult line ≈ −15
 *   announcer             ≈ −13
 *   explosions nearby     ≈ −13
 *   KO                    ≈ −16
 *   match stingers        ≈ −15
 *   enemy fire (3 m)      ≈ −19        your own fire ≈ −24 (constant, so it sits low)
 *   own hero voice        ≈ −20        other ability lines ≈ −24 at 3 m
 *   hit ticks             ≈ −24 as a stream, bright enough to cut through your own gun
 *   enemy footsteps (3 m) ≈ −26        allies ≈ −35, yours ≈ −38
 *   menus                 ≈ −30 … −44
 *
 * Raw targets per cue: weapons −17 as a firing stream (per shot lower for fast guns), abilities
 * −17, explosions −12, heroic themes −14, hostile −11, teases −22, feedback −14 … −30.
 */
export const TRIM = {
  fire: { volt: 8, ember: 0, anvil: 9, tether: 1, hog: -3.5, orb: 10, bombard: 11, dart: 7, scoped: 8.5 },
  ability: {
    helix: 8,
    bioticField: 8,
    visor: 13,
    jumpJet: 0,
    concussion: 5,
    barrage: 3.5,
    gateway: 10,
    maelstrom: 5.5,
    chain: 10,
    snare: 12.5,
    hog: 4,
    jetDash: 10,
    beacon: 4,
    amplifier: 0,
    sleepDart: 10.5,
    grenade: 14,
    surge: 0,
  },
  boom: { rocket: -1, helix: -1, missile: 1.5, concussion: 1.5, bio: 10, maelstrom: 0, soulSever: -2.5 },
  impact: { blade: 11, orb: 13, snare: 5, teleport: 9, ricochet: 16, whiz: 12, hook: 4, bounce: 10, missileLaunch: 14, snareArmed: 17, portal: 12 },
  world: { wardUp: 12, wardDown: 14, wardBreak: 4, sprint: 17, pickup: 4.5, revive: 2, lullaby: 6.5, surged: 1.5 },
  handling: { melee: 14, dryFire: 14.5, scope: 16.5 },
  feedback: { hit: 9, crit: 8, ko: 4, assist: 6, heal: 13, healed: 8, ultReady: 5, death: 0, respawn: 4 },
  hurt: { shield: 17, armor: 8, health: 6 },
  status: { stun: 4, sleep: 8, antiHeal: 10, boost: 12, linked: 7 },
  step: { light: 8, mid: 4.5, heavy: 4 },
  jump: { light: 15, mid: 15, heavy: 15 },
  land: { light: 3, mid: 0, heavy: -2 },
  match: { tick: 14, drum: 6, unlock: -2.5, roundIntro: 0, captured: 0, lost: -12, roundWon: -2.5, roundLost: -4.5, victory: -2, defeat: -1.5 },
  ui: { hover: 13.5, press: 11.5, back: 12, toggle: 14.5, panel: 16, toast: 6, toastError: 9, chat: 16 },
  /**
   * Continuous voices. Beds that never stop (room tone, the objective's hum, the idle payload)
   * sit far below everything; a payload being pushed rises ~3 dB over its idle; alarms you
   * must act on (the enemy capturing) sit well above them.
   */
  loop: {
    jets: 0,
    ward: -11,
    breather: 0,
    levitate: 6,
    climb: 12,
    motor: 0,
    barrage: -4,
    visor: 10,
    bioticField: 0,
    portal: 4,
    beacon: 4,
    amplifier: -7,
    rocket: -2,
    missile: 0,
    orb: 0,
    hook: 0,
    heartbeat: 0,
    captureOurs: 7,
    captureTheirs: 15,
    contested: 6.5,
    pointHum: -9.5,
    payload: -3,
    overtime: 0,
    roomTone: -13,
  },
};
